import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { memoize } from "./serverMemo";

export type PublicPick = {
  id: string;
  team_home: string;
  team_away: string;
  league: string | null;
  kickoff_time: string;
  market_type: string;
  predicted_outcome: string;
  odds: number | null;
  confidence_score: number | null;
  reason: string | null;
};

function shortReason(text: string | null | undefined): string | null {
  if (!text) return null;
  const clean = text.replace(/\s+/g, " ").trim();
  const first = clean.split(/(?<=[.!?])\s/)[0] ?? clean;
  return first.length > 220 ? `${first.slice(0, 217)}…` : first;
}

/**
 * Public feed (owner decision): every upcoming pick in the server-rendered
 * public lists ships with market, outcome, odds and confidence so search
 * engines index real content. Deep analysis (strategy signals, EV, Kelly)
 * stays credit-gated via get_prediction_intelligence. Single-pick lookups
 * (match summary, fixture) still use gatePick for non-list surfaces.
 */
export const FREE_PUBLIC_PICKS = Number.POSITIVE_INFINITY;
function gatePick<T extends { kickoff_time: string; predicted_outcome: string; reason: string | null; market_type: string; odds: number | null; confidence_score: number | null }>(p: T, open: boolean): T {
  if (open || new Date(p.kickoff_time).getTime() <= Date.now()) return p;
  // Market + odds + confidence together give the pick away — strip them all.
  return { ...p, predicted_outcome: "", reason: null, market_type: "", odds: null, confidence_score: null };
}

type Row = { id: unknown; team_home: unknown; team_away: unknown; league: unknown; kickoff_time: unknown; market_type: unknown; predicted_outcome: unknown; odds: unknown; confidence_score: unknown; explanation: unknown };
function toPublic(p: Row): PublicPick {
  return {
    id: p.id as string,
    team_home: p.team_home as string,
    team_away: p.team_away as string,
    league: (p.league as string | null) ?? null,
    kickoff_time: p.kickoff_time as string,
    market_type: p.market_type as string,
    predicted_outcome: p.predicted_outcome as string,
    odds: p.odds == null ? null : Number(p.odds),
    confidence_score: p.confidence_score == null ? null : Number(p.confidence_score),
    reason: shortReason(p.explanation as string | null),
  };
}
const PICK_COLS = "id, team_home, team_away, league, kickoff_time, market_type, predicted_outcome, odds, confidence_score, explanation";

/** Upcoming approved picks, public-safe fields only — server-rendered for visitors and crawlers. */
export const getPublicUpcomingPicks = createServerFn({ method: "GET" }).handler(() => memoize("upcoming", 60_000, async () => {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("predictions")
      .select(PICK_COLS)
      .eq("status", "approved")
      .gte("kickoff_time", new Date().toISOString())
      .order("kickoff_time", { ascending: true })
      .limit(60);
    if (error) {
      console.error("public picks", error.message);
      return [] as PublicPick[];
    }
    return (data ?? []).map((p) => toPublic(p as unknown as Row)).map((p, i) => gatePick(p, i < FREE_PUBLIC_PICKS));
  } catch (e) {
    console.error("public picks failed", e);
    return [] as PublicPick[];
  }
}));

/** Upcoming public picks whose teams or league match any of the given words — for league/team entity pages. */
export const getEntityPicks = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ field: z.enum(["league", "team"]), terms: z.array(z.string().min(2).max(60)).min(1).max(8) }).parse(d))
  .handler(({ data }) => memoize(`entity:${data.field}:${data.terms.join("|")}`, 120_000, async () => {
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const clean = data.terms.map((t) => t.replace(/[^a-zA-Z0-9 \-]/g, "").trim()).filter(Boolean);
      if (!clean.length) return [] as PublicPick[];
      const cols = data.field === "league" ? ["league"] : ["team_home", "team_away"];
      const or = clean.flatMap((t) => cols.map((c) => `${c}.ilike.%${t}%`)).join(",");
      const { data: rows, error } = await supabaseAdmin
        .from("predictions")
        .select(PICK_COLS)
        .eq("status", "approved")
        .gte("kickoff_time", new Date().toISOString())
        .or(or)
        .order("kickoff_time", { ascending: true })
        .limit(40);
      if (error) {
        console.error("entity picks", error.message);
        return [] as PublicPick[];
      }
      return (rows ?? []).map((p) => toPublic(p as unknown as Row));
    } catch (e) {
      console.error("entity picks failed", e);
      return [] as PublicPick[];
    }
  }));

/** Today's member pick: highest-confidence approved pick kicking off in the next 24 hours. */
export const getDailyMemberPick = createServerFn({ method: "GET" }).handler(() => memoize("daily-member-pick", 300_000, async () => {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const now = new Date();
    const { data, error } = await supabaseAdmin
      .from("predictions")
      .select(PICK_COLS)
      .eq("status", "approved")
      .gte("kickoff_time", now.toISOString())
      .lte("kickoff_time", new Date(now.getTime() + 36 * 3600_000).toISOString())
      .limit(200);
    if (error) return null;
    const { chooseDailyPick } = await import("./dailyPick");
    return chooseDailyPick((data ?? []).map((p) => toPublic(p as unknown as Row)), now);
  } catch (e) {
    console.error("daily pick failed", e);
    return null;
  }
}));

export type PublicMatchSummary = {
  pick: PublicPick | null;
  recent: { home: string[]; away: string[] };
};

/** Server-rendered match summary for the match preview page. */
export const getPublicMatchSummary = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ home: z.string().min(1).max(120), away: z.string().min(1).max(120) }).parse(d))
  .handler(async ({ data }): Promise<PublicMatchSummary> => {
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: preds } = await supabaseAdmin
        .from("predictions")
        .select("id, team_home, team_away, league, kickoff_time, market_type, predicted_outcome, odds, confidence_score, explanation")
        .eq("status", "approved")
        .eq("team_home", data.home)
        .eq("team_away", data.away)
        .order("kickoff_time", { ascending: false })
        .limit(1);
      const p = preds?.[0];
      const recentFor = async (team: string) => {
        // Two parameterized queries instead of an interpolated .or() filter, so
        // team names can never inject filter syntax.
        const today = new Date().toISOString().slice(0, 10);
        const side = (col: "team_home" | "team_away") =>
          supabaseAdmin
            .from("matches")
            .select("match_date, team_home, team_away, home_score, away_score")
            .eq(col, team)
            .not("home_score", "is", null)
            .not("away_score", "is", null)
            .lt("match_date", today)
            .order("match_date", { ascending: false })
            .limit(10);
        const [a, b] = await Promise.all([side("team_home"), side("team_away")]);
        const ms = [...(a.data ?? []), ...(b.data ?? [])]
          .sort((x, y) => String(y.match_date).localeCompare(String(x.match_date)))
          .slice(0, 10);
        return (ms ?? []).map((m) => {
          const h = m.team_home === team;
          return `${m.match_date}: ${h ? "vs" : "at"} ${h ? m.team_away : m.team_home} ${h ? m.home_score : m.away_score}–${h ? m.away_score : m.home_score}`;
        });
      };
      const [home, away] = await Promise.all([recentFor(data.home), recentFor(data.away)]);
      return {
        pick: p
          ? gatePick({
              id: p.id as string,
              team_home: p.team_home as string,
              team_away: p.team_away as string,
              league: (p.league as string | null) ?? null,
              kickoff_time: p.kickoff_time as string,
              market_type: p.market_type as string,
              predicted_outcome: p.predicted_outcome as string,
              odds: p.odds == null ? null : Number(p.odds),
              confidence_score: p.confidence_score == null ? null : Number(p.confidence_score),
              reason: shortReason(p.explanation as string | null),
            }, false)
          : null,
        recent: { home, away },
      };
    } catch (e) {
      console.error("match summary failed", e);
      return { pick: null, recent: { home: [], away: [] } };
    }
  });

export type PublicFixture = PublicPick & { actual_outcome: string | null; home_score: number | null; away_score: number | null };

const slugifyName = (v: string) =>
  (v || "").toLowerCase().trim().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/** Server-rendered fixture (upcoming OR finished) so fixture URLs stay permanent, crawlable pages. */
export const getPublicFixture = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ slug: z.string().min(3).max(200) }).parse(d))
  .handler(async ({ data }): Promise<PublicFixture | null> => {
    try {
      const m = data.slug.match(/^(.+)-vs-(.+?)(?:-(\d{4}-\d{2}-\d{2}))?$/);
      if (!m) return null;
      const [, home, away, date] = m;
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      let q = supabaseAdmin
        .from("predictions")
        .select("id, team_home, team_away, league, kickoff_time, market_type, predicted_outcome, odds, confidence_score, explanation, actual_outcome")
        .eq("status", "approved")
        .order("kickoff_time", { ascending: false })
        .limit(500);
      if (date) q = q.gte("kickoff_time", `${date}T00:00:00Z`).lte("kickoff_time", `${date}T23:59:59Z`);
      const { data: rows } = await q;
      const p = (rows ?? []).find((r) => slugifyName(r.team_home as string) === home && slugifyName(r.team_away as string) === away);
      if (!p) return null;
      const { data: mrow } = await supabaseAdmin
        .from("matches")
        .select("home_score, away_score")
        .eq("team_home", p.team_home as string)
        .eq("team_away", p.team_away as string)
        .eq("match_date", (p.kickoff_time as string).slice(0, 10))
        .not("home_score", "is", null)
        .limit(1);
      const score = mrow?.[0];
      return gatePick({
        home_score: score?.home_score == null ? null : Number(score.home_score),
        away_score: score?.away_score == null ? null : Number(score.away_score),
        id: p.id as string,
        team_home: p.team_home as string,
        team_away: p.team_away as string,
        league: (p.league as string | null) ?? null,
        kickoff_time: p.kickoff_time as string,
        market_type: p.market_type as string,
        predicted_outcome: p.predicted_outcome as string,
        odds: p.odds == null ? null : Number(p.odds),
        confidence_score: p.confidence_score == null ? null : Number(p.confidence_score),
        reason: shortReason(p.explanation as string | null),
        actual_outcome: (p.actual_outcome as string | null) ?? null,
      }, false);
    } catch (e) {
      console.error("public fixture failed", e);
      return null;
    }
  });

export type PublicTrackRecord = {
  won: number;
  lost: number;
  winRate: number;
  recent: (PublicPick & { was_correct: boolean })[];
};

/** Graded track record (win rate + latest graded picks), server-rendered for crawlers. */
export const getPublicTrackRecord = createServerFn({ method: "GET" }).handler((): Promise<PublicTrackRecord> => memoize("track", 60_000, async (): Promise<PublicTrackRecord> => {
  const empty: PublicTrackRecord = { won: 0, lost: 0, winRate: 0, recent: [] };
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const base = () => supabaseAdmin.from("predictions").select("id", { count: "exact", head: true }).eq("status", "approved");
    const [w, l, r] = await Promise.all([
      base().eq("was_correct", true),
      base().eq("was_correct", false),
      supabaseAdmin
        .from("predictions")
        .select("id, team_home, team_away, league, kickoff_time, market_type, predicted_outcome, odds, confidence_score, explanation, was_correct")
        .eq("status", "approved")
        .not("was_correct", "is", null)
        .order("kickoff_time", { ascending: false })
        .limit(30),
    ]);
    const won = w.count ?? 0;
    const lost = l.count ?? 0;
    return {
      won,
      lost,
      winRate: won + lost > 0 ? Math.round((won / (won + lost)) * 1000) / 10 : 0,
      recent: (r.data ?? []).map((p) => ({
        id: p.id as string,
        team_home: p.team_home as string,
        team_away: p.team_away as string,
        league: (p.league as string | null) ?? null,
        kickoff_time: p.kickoff_time as string,
        market_type: p.market_type as string,
        predicted_outcome: p.predicted_outcome as string,
        odds: p.odds == null ? null : Number(p.odds),
        confidence_score: p.confidence_score == null ? null : Number(p.confidence_score),
        reason: shortReason(p.explanation as string | null),
        was_correct: p.was_correct === true,
      })),
    };
  } catch (e) {
    console.error("public track record failed", e);
    return empty;
  }
}));
